'use client';
import { MotionMaybe, useMotionControls } from '@/utils/animation';
import { buttonLinkTargetProps } from '@/utils/buttonLinkTarget';
import { getColorScheme } from '@/utils/getColorScheme';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { Button } from './atoms/Button';
import { MarkDown } from './atoms/MarkDown';

export default function Reviews(props) {
    const {
        Tagline,
        Header,
        Body,
        ButtonLink,
        ButtonText,
        ButtonOpenInNewTab,
        ColorScheme = 'light',
        VerticalSpacing = 'medium',
        Alignment = 'center', // New prop to determine alignment
        Animation = 'fade',
        Duration = 0.6,
        AnimationGroup = 'content',
        EditorPreview = false,
    } = props;

    const vSpace = getVerticalSpacing(VerticalSpacing);
    const {
        backgroundColor,
        headerColor,
        textColor,
        thirdButton,
        taglineColor,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);

    const isLeftAligned = Alignment === 'left';
    const isRightAligned = Alignment === 'right';
    const isCentered = Alignment === 'center';
    const textAlign = isLeftAligned
        ? 'text-left'
        : isRightAligned
        ? 'text-right'
        : 'text-center';
    const sideTextAlign = isRightAligned ? 'text-left' : textAlign;
    const durationValue = Number(Duration) || 0.6;
    const normalizedGroup = (() => {
        const v = String(AnimationGroup ?? '')
            .toLowerCase()
            .trim();
        if (
            [
                'cta',
                'ctas',
                'cta-only',
                'ctas-only',
                'button',
                'buttons',
            ].includes(v)
        )
            return 'cta';
        if (v === 'all') return 'all';
        return 'content';
    })();

    const { enableAnim, enableChild, childMotionProps, remountKey } =
        useMotionControls({
            preset: Animation,
            duration: durationValue,
            editorPreview: EditorPreview,
            group: normalizedGroup === 'all' ? 'content' : normalizedGroup,
            keyBase: 'trust-badge',
        });
    const shouldAnimate = (group) =>
        enableAnim && (normalizedGroup === 'all' || enableChild(group));

    return (
        <div
            className={`editable-component relative flex items-center ${backgroundColor} ${vSpace}`}
            style={backgroundStyle}
        >
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                {isCentered ? (
                    <div className="mx-auto flex max-w-2xl flex-col justify-center text-center">
                        <MotionMaybe
                            enable={shouldAnimate('content')}
                            nodeKey={`${remountKey}-center-text`}
                            motionProps={childMotionProps}
                            className="mx-auto flex max-w-2xl flex-col justify-center text-center"
                        >
                            <MarkDown
                                variant="h3"
                                className={`font-display text-base uppercase leading-7 tracking-wide ${taglineColor}`}
                            >
                                {Tagline}
                            </MarkDown>
                            <MarkDown
                                variant="h2"
                                className={`mt-2 font-display text-3xl tracking-tight ${headerColor} sm:text-4xl sm:leading-tight`}
                            >
                                {Header}
                            </MarkDown>
                            {Body && (
                                <div className="z-40">
                                    <MarkDown
                                        className={`mt-6 max-w-2xl text-lg font-text leading-8 ${textColor} ${textAlign}`}
                                    >
                                        {Body}
                                    </MarkDown>
                                </div>
                            )}
                            {ButtonText && (
                                <MotionMaybe
                                    enable={shouldAnimate('cta')}
                                    nodeKey={`${remountKey}-center-cta`}
                                    motionProps={childMotionProps}
                                    className="relative z-40 mt-10 flex w-full justify-center gap-4 lg:mt-16"
                                >
                                    <Button
                                        asChild
                                        variant={thirdButton}
                                        size="md"
                                        className="text-center"
                                        href={ButtonLink}
                                        {...buttonLinkTargetProps(
                                            ButtonOpenInNewTab
                                        )}
                                    >
                                        <a>{ButtonText}</a>
                                    </Button>
                                </MotionMaybe>
                            )}
                        </MotionMaybe>
                        <MotionMaybe
                            enable={shouldAnimate('content')}
                            nodeKey={`${remountKey}-center-badge`}
                            motionProps={childMotionProps}
                        >
                            <div
                                className="nj-badge mx-auto mt-16 w-full max-w-2xl rounded-[min(1.5rem,var(--shape-radius))] bg-neutral-50 p-8"
                                data-show-reviews="1"
                            ></div>
                        </MotionMaybe>
                    </div>
                ) : (
                    <div
                        className={`flex flex-col justify-center lg:grid lg:grid-cols-2 lg:items-center lg:gap-12 ${
                            isLeftAligned || isRightAligned ? '' : 'text-center'
                        }`}
                    >
                        <MotionMaybe
                            enable={shouldAnimate('content')}
                            nodeKey={`${remountKey}-side-text`}
                            motionProps={childMotionProps}
                            className={`mx-auto flex max-w-2xl flex-col justify-center ${sideTextAlign} ${
                                isRightAligned
                                    ? 'ml-auto items-start'
                                    : isLeftAligned
                                    ? 'items-start'
                                    : 'items-center'
                            }`}
                        >
                            <MarkDown
                                variant="h3"
                                className={`font-display text-base uppercase leading-7 tracking-wide ${taglineColor}`}
                            >
                                {Tagline}
                            </MarkDown>
                            <MarkDown
                                variant="h2"
                                className={`mt-2 font-display text-3xl tracking-tight ${headerColor} sm:text-4xl sm:leading-tight`}
                            >
                                {Header}
                            </MarkDown>
                            {Body && (
                                <div className="z-40">
                                    <MarkDown
                                        className={`mt-6 max-w-2xl text-lg font-text leading-8 ${textColor} ${sideTextAlign}`}
                                    >
                                        {Body}
                                    </MarkDown>
                                </div>
                            )}
                            {ButtonText && (
                                <MotionMaybe
                                    enable={shouldAnimate('cta')}
                                    nodeKey={`${remountKey}-side-cta`}
                                    motionProps={childMotionProps}
                                    className={`relative z-40 mt-10 flex max-w-2xl flex-wrap items-center gap-4 lg:mt-16 ${
                                        isRightAligned
                                            ? 'justify-start'
                                            : isLeftAligned
                                            ? 'justify-start'
                                            : 'justify-center'
                                    }`}
                                >
                                    <Button
                                        asChild
                                        variant={thirdButton}
                                        size="md"
                                        className="text-center"
                                        href={ButtonLink}
                                        {...buttonLinkTargetProps(
                                            ButtonOpenInNewTab
                                        )}
                                    >
                                        <a>{ButtonText}</a>
                                    </Button>
                                </MotionMaybe>
                            )}
                        </MotionMaybe>
                        <MotionMaybe
                            enable={shouldAnimate('content')}
                            nodeKey={`${remountKey}-side-badge`}
                            motionProps={childMotionProps}
                            className={
                                isRightAligned
                                    ? 'lg:order-first lg:justify-self-start'
                                    : ''
                            }
                        >
                            <div
                                className={`nj-badge mt-16 h-full w-full max-w-2xl rounded-[min(1.5rem,var(--shape-radius))] bg-neutral-50 p-8 lg:mt-0 lg:self-start ${
                                    isRightAligned ? 'mr-auto' : 'mx-auto'
                                }`}
                                data-show-reviews="1"
                            ></div>
                        </MotionMaybe>
                    </div>
                )}
            </div>
        </div>
    );
}
